import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { ClickAwayListener } from '@mui/material';
import { Switch } from 'apps/sigma2-ui/src/components/ui/Switch';
import { RadioGroup } from '@mui/material';
import { FormControl } from '@mui/material';
import { FormControlLabel } from '@mui/material';
import { FormLabel } from '@mui/material';
import { Radio } from '@mui/material';
import { Paper } from '@mui/material';
import { Popper } from '@mui/material';
import { Stack } from '@mui/material';
import { Tooltip } from '@mui/material';
import { Typography } from '@mui/material';
import { GridActionsCell } from '@mui/x-data-grid-pro';
import { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { GridRowId } from '@mui/x-data-grid-pro';
import { GridActionsCellItem } from '@mui/x-data-grid-pro';
import { Toolbar } from '@mui/x-data-grid-pro';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { ToolbarButton } from '@mui/x-data-grid-pro';
import { useGridApiContext } from '@mui/x-data-grid-pro';
import { GridColDef } from '@mui/x-data-grid-pro';
import { Add } from '@mui/icons-material';
import { Remove } from '@mui/icons-material';
import { PrimaryButtonLightBg } from 'apps/sigma2-ui/src/components/PrimaryButtonLightBg';
import { CategoricalValueRows } from 'libs/end-to-end/src/data-generator/data-generator-input';
import { DataGeneratorCategoricalColumnDetails } from 'libs/end-to-end/src/data-generator/data-generator-input';
import { DataGeneratorRows } from 'libs/end-to-end/src/data-generator/data-generator-input';
import { DataGeneratorColumnDataClass } from 'libs/end-to-end/src/data-generator/data-generator-input';
import { DataGeneratorNumericalColumnDetails } from 'libs/end-to-end/src/data-generator/data-generator-input';
import { DataGeneratorColumnDetails } from 'libs/end-to-end/src/data-generator/data-generator-input';
import { DataGeneratorColumnDataType } from 'libs/end-to-end/src/data-generator/data-generator-input';
import { NewColumnCategoricalValuesDataGrid } from 'apps/sigma2-ui/src/components/data-generator/NewColumnCategoricalValuesDataGrid';
import { Setter } from 'libs/end-to-end/src/ui-types';
import { SetterWithReducer } from 'libs/end-to-end/src/ui-types';
import { Color } from 'libs/end-to-end/src/types/branding/color';
import { safeParseInt } from 'libs/end-to-end/src/math';
import { TextFieldCompat } from 'apps/sigma2-ui/src/components/MuiCompat';

interface NewColumnsDataGridProps {
  categoricalValueRows: CategoricalValueRows;
  setCategoricalValueRows: SetterWithReducer<CategoricalValueRows>;
  isLoading: boolean;
  addColumn: (column: DataGeneratorColumnDetails) => void;
  setError: Setter<string | null>;
  newPanelOpen: boolean;
  setNewPanelOpen: SetterWithReducer<boolean>;
  newColumnDataType: DataGeneratorColumnDataType;
  setNewColumnDataType: Setter<DataGeneratorColumnDataType>;
  newColumnDataClass: DataGeneratorColumnDataClass;
  setNewColumnDataClass: Setter<DataGeneratorColumnDataClass>;
  name: string;
  setName: Setter<string>;
  description: string;
  setDescription: Setter<string>;
  color: string;
  setColor: Setter<string>;
  numericMin: string;
  setNumericMin: Setter<string>;
  numericMax: string;
  setNumericMax: Setter<string>;
  editingColumnId: GridRowId | undefined;
  setEditingColumnId: Setter<GridRowId | undefined>;
  updateColumn: (id: GridRowId, column: DataGeneratorColumnDetails) => void;
  varyAcrossObservations: boolean;
  varyAcrossPeriods: boolean;
  setVaryAcrossObservations: Setter<boolean>;
  setVaryAcrossPeriods: Setter<boolean>;
  rowSelectionModel: GridRowSelectionModel | undefined;
  rows: DataGeneratorRows;
  setRows: SetterWithReducer<DataGeneratorRows>;
  onColumnsRemoved?: (columnNames: string[]) => void;
}

const CustomToolbar: React.FC<NewColumnsDataGridProps> = ({
  categoricalValueRows,
  setCategoricalValueRows,
  isLoading,
  addColumn,
  setError,
  newPanelOpen,
  setNewPanelOpen,
  newColumnDataType,
  setNewColumnDataType,
  newColumnDataClass,
  setNewColumnDataClass,
  name,
  setName,
  description,
  setDescription,
  color,
  setColor,
  numericMin,
  setNumericMin,
  numericMax,
  setNumericMax,
  updateColumn,
  editingColumnId,
  setEditingColumnId,
  varyAcrossObservations,
  varyAcrossPeriods,
  setVaryAcrossObservations,
  setVaryAcrossPeriods,
  rowSelectionModel,
  rows,
  setRows,
  onColumnsRemoved,
}) => {
  const apiRef = useGridApiContext();

  const [newPanelTriggerDomNode, setNewPanelTriggerDomNode] =
    useState<HTMLButtonElement | null>(null);

  const onRefChange = useCallback((node: HTMLButtonElement | null) => {
    setNewPanelTriggerDomNode(node); // This triggers a re-render
  }, []);

  const handleClose = useCallback(() => {
    setNewPanelOpen(() => false);
  }, [setNewPanelOpen]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const formData = new FormData(event.target as HTMLFormElement);
    apiRef.current.updateRows([
      {
        id: 5,
        commodity: formData.get('commodity'),
        quantity: Number(formData.get('quantity')),
        unitPrice: Number(formData.get('unitPrice')),
      },
    ]);
    handleClose();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      handleClose();
    }
  };

  const handleRemoveValue = useCallback(() => {
    if (!rowSelectionModel) {
      return;
    }

    setRows((prev) => {
      const toRemove =
        rowSelectionModel.type === 'include'
          ? prev.filter((row) => rowSelectionModel.ids.has(row.id))
          : prev.filter((row) => !rowSelectionModel.ids.has(row.id));

      const removedNames = toRemove.map((r) => r.name);

      if (removedNames.length > 0) {
        onColumnsRemoved?.(removedNames);
      }

      if (rowSelectionModel.type === 'include') {
        return prev.filter((row) => !rowSelectionModel.ids.has(row.id));
      }
      return prev.filter((row) => rowSelectionModel.ids.has(row.id));
    });
  }, [rowSelectionModel, setRows, onColumnsRemoved]);

  const hasSelection = useMemo(() => {
    if (!rowSelectionModel) {
      return false;
    }

    if (rowSelectionModel.type === 'include') {
      return rowSelectionModel.ids.size > 0;
    }

    return rows.length > rowSelectionModel.ids.size;
  }, [rowSelectionModel, rows.length]);

  const language = useLanguage();

  const handleNewColumnDataTypeChanged = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const valueParsed = DataGeneratorColumnDataType.safeParse(
        (event.target as HTMLInputElement).value,
      );
      if (valueParsed.success) {
        setNewColumnDataType(valueParsed.data);
        if (
          valueParsed.data === 'numerical' &&
          newColumnDataClass === 'dimension'
        ) {
          setNewColumnDataClass('controlVariable');
        }
      }
    },
    [newColumnDataClass, setNewColumnDataClass, setNewColumnDataType],
  );

  const handleNewColumnDataClassChanged = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const valueParsed = DataGeneratorColumnDataClass.safeParse(
        (event.target as HTMLInputElement).value,
      );
      if (valueParsed.success) {
        setNewColumnDataClass(valueParsed.data);
      }
    },
    [setNewColumnDataClass],
  );

  const filterNewColumnDataClassOpts = useCallback(
    (dataClass: DataGeneratorColumnDataClass) =>
      newColumnDataType === 'numerical' && dataClass === 'dimension'
        ? false
        : true,
    [newColumnDataType],
  );

  const handleNameChanged = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setName((event.target as HTMLInputElement).value);
    },
    [setName],
  );

  const handleDescriptionChanged = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setDescription((event.target as HTMLInputElement).value);
    },
    [setDescription],
  );

  const handleColorChanged = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setColor((event.target as HTMLInputElement).value);
    },
    [setColor],
  );

  const handleNumericMinChanged = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setNumericMin((event.target as HTMLInputElement).value);
    },
    [setNumericMin],
  );

  const handleNumericMaxChanged = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setNumericMax((event.target as HTMLInputElement).value);
    },
    [setNumericMax],
  );

  const handleReset = useCallback(() => {
    setError(null);
    setNewColumnDataType('categorical');
    setNewColumnDataClass('dimension');
    setNumericMin('0');
    setNumericMax('100');
    setName('');
    setDescription('');
    setCategoricalValueRows(() => [{ id: 0, value: 'Default' }]);
    setVaryAcrossObservations(false);
    setVaryAcrossPeriods(false);
    setEditingColumnId(undefined);
  }, [
    setCategoricalValueRows,
    setDescription,
    setEditingColumnId,
    setError,
    setName,
    setNewColumnDataClass,
    setNewColumnDataType,
    setNumericMax,
    setNumericMin,
    setVaryAcrossObservations,
    setVaryAcrossPeriods,
  ]);

  const isValid = useMemo(() => {
    const baseOptionsSet =
      name.trim().length > 0 && description.trim().length > 0;
    if (!baseOptionsSet) {
      return false;
    }

    if (newColumnDataType === 'categorical') {
      return (
        categoricalValueRows.filter((v) => v.value.trim().length > 0).length >=
        2
      );
    }

    return numericMin.trim().length > 0 && numericMax.trim().length > 0;
  }, [
    categoricalValueRows,
    description,
    name,
    newColumnDataType,
    numericMax,
    numericMin,
  ]);

  useEffect(() => {
    if (newColumnDataClass === 'dimension') {
      if (varyAcrossObservations) {
        setVaryAcrossObservations(false);
      }

      if (varyAcrossPeriods) {
        setVaryAcrossPeriods(false);
      }
    }
  }, [
    newColumnDataClass,
    setVaryAcrossObservations,
    setVaryAcrossPeriods,
    varyAcrossObservations,
    varyAcrossPeriods,
  ]);

  const handleSaveColumn = useCallback(() => {
    const colorParsed = Color.safeParse(color);
    if (!colorParsed.success) {
      //setError('Invalid color specified');
      //return;
    }

    const otherRows =
      editingColumnId !== undefined
        ? rows.filter((r) => r.id !== editingColumnId)
        : rows;

    if (
      otherRows
        .map((r) => r.name.trim().toLowerCase())
        .includes(name.trim().toLowerCase())
    ) {
      setError(`Column ${name} already exists`);
      return;
    }

    if (newColumnDataType === 'categorical') {
      const colDetails: DataGeneratorCategoricalColumnDetails = {
        name,
        description,
        color: 'Aqua', //colorParsed.data,
        dataType: 'categorical',
        dataClass: newColumnDataClass,
        values: categoricalValueRows
          .filter((v) => v.value.trim().length > 0)
          .map((v) => v.value),
        varyAcrossObservations,
        varyAcrossPeriods,
      };
      if (editingColumnId !== undefined) {
        updateColumn(editingColumnId, colDetails);
      } else {
        addColumn(colDetails);
      }
    } else {
      if (newColumnDataClass === 'dimension') {
        setError('Invalid data class');
        return;
      }

      const minParsed = safeParseInt(numericMin, 10);
      if (minParsed === null) {
        setError('Invalid min value');
        return;
      }

      const maxParsed = safeParseInt(numericMax, 10);
      if (maxParsed === null) {
        setError('Invalid max value');
        return;
      }

      const colDetails: DataGeneratorNumericalColumnDetails = {
        name,
        description,
        color: 'Aqua', //colorParsed.data,
        dataType: 'numerical',
        dataClass: newColumnDataClass,
        min: minParsed,
        max: maxParsed,
        varyAcrossObservations,
        varyAcrossPeriods,
      };

      if (editingColumnId !== undefined) {
        updateColumn(editingColumnId, colDetails);
      } else {
        addColumn(colDetails);
      }
    }

    handleClose();
    handleReset();
  }, [
    addColumn,
    categoricalValueRows,
    color,
    description,
    editingColumnId,
    handleClose,
    handleReset,
    name,
    newColumnDataClass,
    newColumnDataType,
    numericMax,
    numericMin,
    rows,
    setError,
    updateColumn,
    varyAcrossObservations,
    varyAcrossPeriods,
  ]);

  return (
    <Toolbar>
      <Stack
        direction={'row'}
        spacing={1}
        sx={{ flex: 1, mx: 0.5, alignItems: 'center' }}
      >
        <Typography fontWeight="medium">Columns</Typography>
        <Tooltip title="Add new column">
          <ToolbarButton
            ref={onRefChange}
            aria-describedby="new-panel"
            onClick={() => setNewPanelOpen((prev) => !prev)}
          >
            <Add fontSize="small" />
          </ToolbarButton>
        </Tooltip>

        <Tooltip title="Remove selected columns">
          <ToolbarButton disabled={!hasSelection} onClick={handleRemoveValue}>
            <Remove fontSize="small" />
          </ToolbarButton>
        </Tooltip>

        <Popper
          open={newPanelOpen}
          anchorEl={newPanelTriggerDomNode}
          placement="bottom-start"
          id="new-panel"
          onKeyDown={handleKeyDown}
        >
          <ClickAwayListener onClickAway={handleClose}>
            <Paper
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                width: 600,
                p: 2,
              }}
              elevation={8}
            >
              <Typography fontWeight="bold">
                {editingColumnId !== undefined
                  ? 'Edit column'
                  : 'Add new column'}
              </Typography>
              <form onSubmit={handleSubmit}>
                <Stack spacing={2}>
                  <FormControl required={true}>
                    <FormLabel id="column-data-type-label">Data Type</FormLabel>
                    <RadioGroup
                      row
                      aria-labelledby="column-data-type-label"
                      value={newColumnDataType}
                      onChange={handleNewColumnDataTypeChanged}
                    >
                      {DataGeneratorColumnDataType.options.map((dataType) => (
                        <>
                          <FormControlLabel
                            value={dataType}
                            control={<Radio />}
                            label={language.dataGeneratorDataTypeLabel({
                              dataType,
                            })}
                          />
                        </>
                      ))}
                    </RadioGroup>
                  </FormControl>
                  <FormControl required={true}>
                    <FormLabel id="column-data-class-label">
                      Data Class
                    </FormLabel>
                    <RadioGroup
                      row
                      aria-labelledby="column-data-class-label"
                      value={newColumnDataClass}
                      onChange={handleNewColumnDataClassChanged}
                    >
                      {DataGeneratorColumnDataClass.options
                        .filter(filterNewColumnDataClassOpts)
                        .map((dataClass) => (
                          <>
                            <FormControlLabel
                              value={dataClass}
                              control={<Radio />}
                              label={language.dataGeneratorDataClassLabel({
                                dataClass,
                              })}
                            />
                          </>
                        ))}
                    </RadioGroup>
                  </FormControl>
                  <TextFieldCompat
                    label="Name"
                    name="name"
                    size="small"
                    value={name}
                    onChange={handleNameChanged}
                    autoFocus
                    fullWidth
                    required
                  />
                  <TextFieldCompat
                    label="Description"
                    name="description"
                    size="small"
                    value={description}
                    onChange={handleDescriptionChanged}
                    multiline
                    rows={2}
                    fullWidth
                    required
                  />
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ justifyContent: 'space-between' }}
                  >
                    <FormControlLabel
                      control={
                        <div className="pr-2">
                          <Switch
                            checked={varyAcrossObservations}
                            onChange={() =>
                              setVaryAcrossObservations(!varyAcrossObservations)
                            }
                          />
                        </div>
                      }
                      label="Vary across observations"
                    />
                    <FormControlLabel
                      control={
                        <div className="pr-2">
                          <Switch
                            checked={varyAcrossPeriods}
                            onChange={() =>
                              setVaryAcrossPeriods(!varyAcrossPeriods)
                            }
                          />
                        </div>
                      }
                      label="Vary across periods"
                    />
                  </Stack>
                  <Stack direction="column" spacing={1}>
                    <TextFieldCompat
                      label="Color"
                      name="color"
                      size="small"
                      type="color"
                      value={color}
                      onChange={handleColorChanged}
                    />
                  </Stack>
                  {newColumnDataType === 'categorical' && (
                    <NewColumnCategoricalValuesDataGrid
                      rows={categoricalValueRows}
                      setRows={setCategoricalValueRows}
                      isLoading={isLoading}
                    />
                  )}
                  {newColumnDataType === 'numerical' && (
                    <>
                      <TextFieldCompat
                        label="Min"
                        name="min"
                        size="small"
                        type="number"
                        value={numericMin}
                        onChange={handleNumericMinChanged}
                        fullWidth
                        required
                      />
                      <TextFieldCompat
                        label="Max"
                        name="max"
                        size="small"
                        type="number"
                        value={numericMax}
                        onChange={handleNumericMaxChanged}
                        fullWidth
                        required
                      />
                    </>
                  )}
                  <PrimaryButtonLightBg
                    translatedContent={
                      editingColumnId !== undefined ? 'Save' : 'Add'
                    }
                    onClick={handleSaveColumn}
                    disabled={!isValid}
                  />
                </Stack>
              </form>
            </Paper>
          </ClickAwayListener>
        </Popper>
      </Stack>
    </Toolbar>
  );
};

export const NewColumnsDataGrid: React.FC<{
  rows: DataGeneratorRows;
  setRows: SetterWithReducer<DataGeneratorRows>;
  isLoading: boolean;
  categoricalValueRows: CategoricalValueRows;
  setCategoricalValueRows: SetterWithReducer<CategoricalValueRows>;
  addColumn: (column: DataGeneratorColumnDetails) => void;
  updateColumn: (id: GridRowId, column: DataGeneratorColumnDetails) => void;
  setError: Setter<string | null>;
  onColumnsRemoved?: (columnNames: string[]) => void;
}> = ({
  rows,
  setRows,
  categoricalValueRows,
  setCategoricalValueRows,
  isLoading,
  addColumn,
  setError,
  updateColumn,
  onColumnsRemoved,
}) => {
  const [newPanelOpen, setNewPanelOpen] = useState(false);
  const [newColumnDataType, setNewColumnDataType] =
    useState<DataGeneratorColumnDataType>('categorical');
  const [newColumnDataClass, setNewColumnDataClass] =
    useState<DataGeneratorColumnDataClass>('dimension');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState<string>('');
  const [numericMin, setNumericMin] = useState('0');
  const [numericMax, setNumericMax] = useState('100');
  const [varyAcrossObservations, setVaryAcrossObservations] = useState(false);
  const [varyAcrossPeriods, setVaryAcrossPeriods] = useState(false);
  const [editingColumnId, setEditingColumnId] = useState<GridRowId>();

  const handleEdit = useCallback(
    (id: GridRowId, column: DataGeneratorColumnDetails) => {
      setNewColumnDataType(column.dataType);
      setNewColumnDataClass(column.dataClass);
      setName(column.name);
      setDescription(column.description);
      setColor('#FFF' /*column.color*/);
      setVaryAcrossObservations(column.varyAcrossObservations);
      setVaryAcrossPeriods(column.varyAcrossPeriods);
      if (column.dataType === 'numerical') {
        setNumericMin(String(column.min));
        setNumericMax(String(column.max));
      } else {
        setCategoricalValueRows(() =>
          column.values.map((v, i) => ({
            id: i,
            value: v,
          })),
        );
      }

      setEditingColumnId(id);
      setNewPanelOpen(true);
    },
    [setCategoricalValueRows],
  );

  const handleDelete = useCallback(
    (id: GridRowId) => {
      const removed = rows.find((r) => r.id === id);
      if (removed) {
        onColumnsRemoved?.([removed.name]);
      }
      setRows((prev) => prev.filter((r) => r.id !== id));
    },
    [rows, setRows, onColumnsRemoved],
  );

  const dataGridColDefs: GridColDef<DataGeneratorRows[number]>[] = [
    { field: 'name', headerName: 'Name', width: 200 },
    {
      field: 'description',
      headerName: 'Description',
      width: 300,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      type: 'actions',
      renderCell: (params) => (
        <GridActionsCell {...params}>
          <GridActionsCellItem
            showInMenu={true}
            onClick={() => handleEdit(params.id, params.row.model)}
            label="Edit"
          />
          <GridActionsCellItem
            showInMenu={true}
            onClick={() => handleDelete(params.id)}
            label="Delete"
          />
        </GridActionsCell>
      ),
    },
  ];

  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set<GridRowId>(),
  });

  return (
    <DataGridPro
      columns={dataGridColDefs}
      rows={rows}
      loading={isLoading}
      rowSelectionModel={selectionModel}
      onRowSelectionModelChange={(newModel) => {
        setSelectionModel(newModel);
      }}
      slots={{
        toolbar: () => {
          return (
            <CustomToolbar
              categoricalValueRows={categoricalValueRows}
              setCategoricalValueRows={setCategoricalValueRows}
              addColumn={addColumn}
              isLoading={isLoading}
              setError={setError}
              newPanelOpen={newPanelOpen}
              setNewPanelOpen={setNewPanelOpen}
              newColumnDataType={newColumnDataType}
              setNewColumnDataType={setNewColumnDataType}
              newColumnDataClass={newColumnDataClass}
              setNewColumnDataClass={setNewColumnDataClass}
              name={name}
              setName={setName}
              description={description}
              setDescription={setDescription}
              color={color}
              setColor={setColor}
              numericMin={numericMin}
              setNumericMin={setNumericMin}
              numericMax={numericMax}
              setNumericMax={setNumericMax}
              varyAcrossObservations={varyAcrossObservations}
              setVaryAcrossObservations={setVaryAcrossObservations}
              varyAcrossPeriods={varyAcrossPeriods}
              setVaryAcrossPeriods={setVaryAcrossPeriods}
              setEditingColumnId={setEditingColumnId}
              updateColumn={updateColumn}
              onColumnsRemoved={onColumnsRemoved}
              rows={rows}
              setRows={setRows}
              rowSelectionModel={selectionModel}
              editingColumnId={editingColumnId}
            />
          );
        },
      }}
      showToolbar
      rowReordering
      checkboxSelection
    />
  );
};
